/**
 * PollCreatorDialog
 * -----------------
 * Compose a poll to send into a DM/group conversation. Mirrors the feed's poll
 * creator (question + 2–N options + optional "Other"). On create it returns a
 * normalized poll object; the conversation layer persists it as a poll message.
 */
import React, { useState } from 'react';
import { Plus, X, BarChart3 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const MAX_OPTIONS = 10;

export default function PollCreatorDialog({ open, onOpenChange, onCreate }) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [allowOther, setAllowOther] = useState(false);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setQuestion('');
    setOptions(['', '']);
    setAllowOther(false);
    setAllowMultiple(false);
    setSubmitting(false);
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const updateOption = (index, value) => {
    setOptions(prev => prev.map((o, i) => (i === index ? value : o)));
  };

  const addOption = () => {
    setOptions(prev => (prev.length >= MAX_OPTIONS ? prev : [...prev, '']));
  };

  const removeOption = (index) => {
    setOptions(prev => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== index)));
  };

  const trimmedQuestion = question.trim();
  const validOptions = options.map(o => o.trim()).filter(Boolean);
  const canCreate = trimmedQuestion.length > 0 && validOptions.length >= 2 && !submitting;

  const handleCreate = async () => {
    if (!canCreate) return;
    setSubmitting(true);
    const poll = {
      question: trimmedQuestion,
      options: validOptions.map(text => ({ text, votes: [] })),
      allow_other: allowOther,
      allow_multiple: allowMultiple,
      other_responses: [],
    };
    try {
      await onCreate(poll);
      handleOpenChange(false);
    } catch (err) {
      console.error('Error creating poll:', err);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dm-poll-creator">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span data-ui="dialog-icon-tile" className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-white" />
            </span>
            Create a poll
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">Question</label>
            <Input
              autoFocus
              placeholder="Ask something…"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={300}
              data-testid="dm-poll-question"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 block">Options</label>
            <div className="space-y-2">
              {options.map((opt, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    placeholder={`Option ${index + 1}`}
                    value={opt}
                    onChange={(e) => updateOption(index, e.target.value)}
                    maxLength={150}
                    data-testid={`dm-poll-option-${index}`}
                  />
                  {options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                      data-testid={`dm-poll-remove-option-${index}`}
                      aria-label="Remove option"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {options.length < MAX_OPTIONS && (
              <button
                type="button"
                onClick={addOption}
                data-ui="link"
                className="mt-2 inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 font-medium"
                data-testid="dm-poll-add-option"
              >
                <Plus className="w-4 h-4" /> Add option
              </button>
            )}
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allowOther}
              onChange={(e) => setAllowOther(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              data-testid="dm-poll-allow-other"
            />
            <span className="text-sm text-slate-600 dark:text-slate-300">Allow a free-text “Other” answer</span>
          </label>

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allowMultiple}
              onChange={(e) => setAllowMultiple(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              data-testid="dm-poll-allow-multiple"
            />
            <span className="text-sm text-slate-600 dark:text-slate-300">Allow multiple selections</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)} data-testid="dm-poll-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!canCreate}
            className="bg-blue-500 hover:bg-blue-600"
            data-testid="dm-poll-create"
          >
            {submitting ? 'Creating…' : 'Create poll'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
